import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: t('header.about'), href: '#about' },
    { name: t('header.products'), href: '#products' },
    { name: t('header.process'), href: '#how-it-works' },
    { name: t('header.specifications'), href: '#technical-specs' },
    { name: t('header.contact'), href: '#contact' },
  ];

  return (
    <>
      <motion.header 
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <motion.a 
              href="#hero" 
              className="text-2xl font-light text-arctic-blue tracking-wider"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ARCTIC ATLAS
            </motion.a>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {menuItems.map((item, index) => (
                <motion.a 
                  key={item.name}
                  href={item.href} 
                  className="text-gray-600 hover:text-arctic-blue font-light transition-colors duration-300 text-sm tracking-wide"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  {item.name}
                </motion.a>
              ))}
              <motion.a 
                href="#contact" 
                className="ml-4 text-arctic-blue border border-arctic-blue px-6 py-2 hover:bg-arctic-blue hover:text-white transition-all duration-300 font-light text-sm whitespace-nowrap"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('header.getQuote')}
              </motion.a>
              <div className="ml-4">
                <LanguageSwitcher />
              </div>
            </nav>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-arctic-blue"
              aria-label="Toggle menu"
            >
              <motion.div
                animate={isMenuOpen ? "open" : "closed"}
                className="w-6 h-5 flex flex-col justify-between"
              >
                <motion.span 
                  className="block h-[2px] w-full bg-arctic-blue origin-left"
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: 45, y: -1 }
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span 
                  className="block h-[2px] w-full bg-arctic-blue"
                  variants={{
                    closed: { opacity: 1 },
                    open: { opacity: 0 }
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span 
                  className="block h-[2px] w-full bg-arctic-blue origin-left"
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: -45, y: 1 }
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="fixed inset-0 z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Backdrop */}
            <motion.div 
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={() => setIsMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Menu */}
            <motion.nav 
              className="absolute right-0 top-0 h-full w-3/4 max-w-sm bg-white shadow-xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="p-6 pt-24">
                {menuItems.map((item, index) => (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    className="block py-4 text-lg font-light text-gray-700 hover:text-arctic-blue transition-colors border-b border-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.name}
                  </motion.a>
                ))}
                <motion.a
                  href="#contact"
                  className="block mt-8 text-center text-arctic-blue border border-arctic-blue px-6 py-3 hover:bg-arctic-blue hover:text-white transition-all duration-300 font-light"
                  onClick={() => setIsMenuOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  {t('header.getQuote')}
                </motion.a>
                <div className="mt-6 flex justify-center">
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;